import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';
import { firstUserErrorCode } from '@/graphql/errors';
import { START_CHECKOUT } from '@/graphql/mutations/billing';
import type { PaidPlanId } from '../types';

export type TransactionOutcome =
  | { transactionId: string; errorCode: null }
  | { transactionId: null; errorCode: string };

type UseStartCheckoutResult = { startCheckout: (plan: PaidPlanId) => Promise<TransactionOutcome> };

/** A Paddle transaction for the plan, made by the server so price and workspace cannot be changed here. */
export function useStartCheckout(): UseStartCheckoutResult {
  const [mutate] = useMutation(START_CHECKOUT);

  const startCheckout = useCallback(
    async (plan: PaidPlanId): Promise<TransactionOutcome> => {
      const { data } = await mutate({
        variables: { input: { plan, idempotencyKey: crypto.randomUUID() } },
      });
      const payload = data?.startCheckout;
      if (payload?.transactionId) return { transactionId: payload.transactionId, errorCode: null };
      return {
        transactionId: null,
        errorCode: firstUserErrorCode(payload?.userErrors) ?? 'UNKNOWN',
      };
    },
    [mutate],
  );

  return { startCheckout };
}
